/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { updateCustomerAddress } from '../api';
import { CustomerAddressesModel } from '../data/models';

/**

Manages the deletion of an address from a list. If the address is the last one in the list, it removes any default properties before deletion. If the address is not the last, it ensures that no default properties are set on the item, transfers them if necessary to another address, and then deletes the address.

*/

export const manageAddressDeletion = async (
  removeId: string,
  addressesList: CustomerAddressesModel[]
) => {
  if (addressesList.length === 1) {
    const address = addressesList[0];
    const isRegionValid = Object.values(address.region as any).every((el) =>
      Boolean(el)
    );

    const region = isRegionValid
      ? {}
      : { region: { ...address.region, regionId: 0 } };

    const result = await updateCustomerAddress({
      addressId: Number(address?.id),
      defaultShipping: false,
      defaultBilling: false,
      ...(region as any),
    });

    return !!result;
  }

  const newDefaultAddress = addressesList.filter((item) => {
    return (
      (item.id !== removeId && (item.defaultBilling || item.defaultShipping)) ||
      item.id !== removeId
    );
  });

  const fallbackDefaultAddress = addressesList[addressesList.length - 1];
  const updatedDefaultAddress =
    newDefaultAddress[0] ||
    (fallbackDefaultAddress?.id !== removeId ? fallbackDefaultAddress : null);

  if (!updatedDefaultAddress || !updatedDefaultAddress.id) return false;

  const result = await updateCustomerAddress({
    addressId: +updatedDefaultAddress.id,
    defaultShipping: true,
    defaultBilling: true,
  });

  return !!result;
};
